import SortableTable, {SORT_ORDER, TableColumn} from "@common/SortableTable";
import {buildTaskFilterOptions} from "@/helpers/task_filter_options";
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {CareJourneyResponse, formatTaskFiltersForApi, triggeringEventTypeDisplayValues} from "@/api/dto/care-journey";
import {dateRange, FilterInvalidate} from "@common/FilterComponent";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {useFilterQuery} from "@/hooks/useFilterQuery";
import {
  DaysOpenOptions, EventSettingOptions,
  EventTypeOptions,
  PriorityOptions, ReferralReviewStatusOptions,
  TaskTypeOptions
} from "@/components/pages/tasks/TasksCommon";
import {FilterView} from "@/api/dto/filters";
import {useInfiniteQuery, useQuery, useQueryClient} from "@tanstack/react-query";
import {buildCareJourneyRows} from "@/helpers/care_journey_rows";
import {useTaskStepName} from "@/hooks/useTaskStepName";
import {useSearchQuery} from "@/hooks/useSearchQuery";
import {SortOrderType} from "@clients/ClientsPage";
import {unionBy} from "lodash-es";

const tableHeaders = [
  "Name",
  "Date of Birth",
  "Event type",
  "Event Setting",
  "Days Open",
  "Task type",
  "Referral Review Status",
  "Next Step",
  "Priority",
  "Last Viewed",
  "Assigned to",
];

export default function ProviderReferralsTab() {
  const {carecoApi} = useContext(CarecoApiContext);
  const [careJourneys, setCareJourneys] = useState<CareJourneyResponse[]>([]);
  const {taskStepDisplayValues, isLoadingTaskStepNames} = useTaskStepName();

  const rosterTotalRef = useRef<number>(0);
  const searchTotalRef = useRef<number>(0);
  const sortKeyRef = useRef<string | undefined>('eventType');
  const sortOrderRef = useRef<SortOrderType | undefined>(SORT_ORDER.DESC);
  const queryClient = useQueryClient();

  const invalidate = useCallback<FilterInvalidate>(() => {
    void queryClient.removeQueries({queryKey: ['careJourneysPreReviewData']});
    setCareJourneys([]);
  }, [queryClient]);

  const {params, queryFnCallback} = useFilterQuery({
    invalidate,
    params: {
      priorities: [],
      eventTypes: [],
      daysOpen: [],
      daysSinceDischarge: [],
      taskDate: {} as dateRange,
      taskTypes: [],
      dueDate: {} as dateRange,
      nextSteps: [],
      lastViewed: {} as dateRange,
      assignees: [],
      attestationStatuses: [],
      referralReviewStatuses: [],
      groups: [],
    },
    multipleRoutesRendered: false,
    invalidateOnUnMount: false,
  });

  const {searchTerm, searchFunction, queryFnSearch} = useSearchQuery({
    invalidate,
    queryFnCallback,
    canInvalidate,
  });

  const {data: assignedToOptions} = useQuery({
    queryKey: ['assignedToOptions'],
    queryFn: () => carecoApi?.getCareJourneyAssignmentOptions(),
    enabled: !!carecoApi,
  });

  const {data: rosters} = useQuery({
    queryKey: ['rosters'],
    queryFn: () => carecoApi?.getRosters(),
    enabled: !!carecoApi,
  });

  const {data: careJourneysPreReviewData, fetchNextPage, isLoading, error, isFetching} = useInfiniteQuery({
    queryKey: ['careJourneysPreReviewData'],
    queryFn: ({pageParam}) => {
      return carecoApi?.getPreReviewCareJourneys(pageParam, {
        sortBy: sortKeyRef.current,
        sortOrder: sortOrderRef.current?.value,
        ...formatTaskFiltersForApi(params),
        searchName: queryFnSearch(),
        searchTaskId: null,
      });
    },
    enabled: !!carecoApi,
    getNextPageParam: (lastPage) => lastPage?.nextPage,
    initialPageParam: 1,
  });

  useEffect(() => {
    if (careJourneysPreReviewData) {
      const fetchedCareJourneys = careJourneysPreReviewData.pages.flatMap(page => page?.data.careJourneys ?? []);
      rosterTotalRef.current = careJourneysPreReviewData.pages[0]?.data.rosterTotal ?? 0;
      searchTotalRef.current = careJourneysPreReviewData.pages[0]?.data.searchTotal ?? 0;
      setCareJourneys((prevCareJourneysPreReview : CareJourneyResponse[]) => prevCareJourneysPreReview ?
        unionBy(fetchedCareJourneys, prevCareJourneysPreReview, 'careJourneyInstanceId') : fetchedCareJourneys);
    }
  }, [careJourneysPreReviewData]);

  const getEventType = useCallback((careJourney: CareJourneyResponse) => {
    return triggeringEventTypeDisplayValues[careJourney.triggeringEventType];
  }, []);


  const tableData = useMemo(() => buildCareJourneyRows(careJourneys, {
    getEventType,
    includeTaskDate: false,
    includeGroupOrg: false,
    includeStatus: false,
    includeReferralReviewStatus: true,
    taskStepDisplayValues,
    findRoster: (rosterId) => rosters?.data?.data?.find((r) => r.id === rosterId),
  }), [careJourneys, getEventType, taskStepDisplayValues, rosters]);

  const handleNextPage = () => {
    if (careJourneysPreReviewData?.pages.at(-1)?.data.careJourneys.length) {
      void fetchNextPage();
    }
  };

  const handleSortChange = (sortKey: TableColumn, sortOrder: SortOrderType) => {
    sortKeyRef.current = sortKey.key;
    sortOrderRef.current = sortOrder;
    invalidate();
  };

  const resetSort = () => {
    sortKeyRef.current = undefined;
    sortOrderRef.current = undefined;
  };

  function canInvalidate() {
    // Provider Referrals searches by client name only, so any term change can invalidate.
    return true;
  }

  return (
    <>
      <SortableTable
        id={FilterView.PROVIDER_REFERRALS_TABLE}
        data={tableData}
        loading={isLoading || isLoadingTaskStepNames}
        fetching={isFetching}
        loadError={error}
        header={tableHeaders}
        sortableColumns={tableData[0] ? [
          tableData[0]['name'],
          tableData[0]['daysOpen'],
          tableData[0]['taskType'],
          tableData[0]['priority'],
        ] : []}
        searchColumns={['name']}
        filterOptions={buildTaskFilterOptions({
          query: {params, invalidate},
          eventTypeOptions: EventTypeOptions,
          eventSettingOptions: EventSettingOptions,
          daysOpenOptions: DaysOpenOptions,
          includeTaskDate: false,
          taskTypeOptions: TaskTypeOptions,
          includeDueDate: true,
          referralReviewStatusOptions: ReferralReviewStatusOptions,
          priorityOptions: PriorityOptions,
          includeLastViewed: true,
          assigneeOptions: assignedToOptions ?? []
        })}
        searchPlaceholder="Search tasks"
        tableOverrides={{
          count: rosterTotalRef.current,
          filteredCount: searchTotalRef.current,
          searchTerm,
          searchFunction,
          sortFunction: handleSortChange,
          resetSortFunction: resetSort,
        }}
        onNextPage={handleNextPage}
      />
    </>
  );

}
